import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import clsx from 'clsx';

import { deleteDocument } from 'containers/Leads/Lead/actions';
import { createDocument, createDocumentFromTemplate, createDocumentComment } from 'api/documents';
import { createQuoteFromLead } from 'api/quotes';
import { createLeadComment } from 'api/lead-record';
import { createInvoiceFromLead, fetchNextInvoiceIdentifier } from 'api/invoices';
import { usePushSiteError } from 'hooks/usePushSiteError';
import { Loading } from  'components/Loading';

import BrochureIcon from 'assets/images/documents/Brochure.svg';
import QuestionnaireIcon from 'assets/images/documents/Questionnaire.svg';
import ProposalIcon from 'assets/images/documents/Proposal.svg';
import ContractIcon from 'assets/images/documents/Contract.svg';
import QuoteIcon from 'assets/images/documents/Quote.svg';
import InvoiceIcon from 'assets/images/documents/Invoice.svg';
import DocumentOptions from './DocumentOptions';
import DocumentModal from './DocumentModal';
import QuoteInvoiceListItem from './QuoteInvoiceListItem';

const DOCUMENT_TYPES = [
  { value: 'brochure', name: 'Brochure', icon: BrochureIcon },
  { value: 'questionnaire', name: 'Questionnaire', icon: QuestionnaireIcon },
  { value: 'proposal', name: 'Proposal', icon: ProposalIcon },
  { value: 'contract', name: 'Contract', icon: ContractIcon },
];

const FILE_TYPES = [
  ...DOCUMENT_TYPES,
  { value: 'quote', name: 'Quote', icon: QuoteIcon },
  { value: 'invoice', name: 'Invoice', icon: InvoiceIcon },
];

export const MODAL_STEPS = {
  CLOSED: 0,
  SELECT_FILE_TYPE: 1,
  FILE_DETAILS: 2,
};

const LeadRecordDocuments = ({
  isLoading,
  documents = [],
  documentTemplates = [],
  quotes = [],
  quoteTemplates = [],
  invoices = [],
  invoiceTemplates = [],
  leadId,
  showNewFileModal = false,
  setShowNewFileModal,
  projectId,
}) => {
  const dispatch = useDispatch();

  const [modalStep, setModalStep] = useState(null);
  const [selectedFileType, setSelectedFileType] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [fileName, setFileName] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [modalTypes, setModalTypes] = useState(DOCUMENT_TYPES);
  const [error, setError] = useState('');

  const { autoNumberingInvoices, id: orgId } = useSelector((state) => state.global.userInfo.organization);

  const pushSiteError = usePushSiteError();

  const filteredDocTemplates = documentTemplates.filter(template => template.kind === selectedFileType);
  const templates = selectedFileType === 'invoice' ?
    invoiceTemplates : selectedFileType === 'quote' ?
    quoteTemplates : filteredDocTemplates;

  const getNextInvoiceNumber = async () => {
    const errMessage = "We weren't able to get your next invoice number. Please refresh and try again.";
    try {
      const response = await fetchNextInvoiceIdentifier({ orgId });
      if (response.ok) {
        setIdentifier(response.data.identifier);
      } else {
        pushSiteError(errMessage);
      }
    } catch (err) {
      pushSiteError(errMessage);
    }
  };


  const shouldAutoNumber = (type) => {
    return (
      !!type &&
      autoNumberingInvoices &&
      ['invoice', 'quote'].includes(type) &&
      !identifier
    );
  };

  const handleOpenModal = (
    types = DOCUMENT_TYPES,
    preselectedType = null,
    step = MODAL_STEPS.SELECT_FILE_TYPE,
  ) => {
    if (shouldAutoNumber(preselectedType)) {
      getNextInvoiceNumber();
    }
    setModalTypes(types);
    if (preselectedType) {
      setSelectedFileType(preselectedType);
      setModalStep(MODAL_STEPS.FILE_DETAILS);
      // Skip straight to file details step.
      return;
    }
    setModalStep(step);
  };

  const handleCloseModal = () => {
    setModalStep(MODAL_STEPS.CLOSED);
    setSelectedFileType(null);
    setSelectedTemplate(null);
    setFileName('');
    setIdentifier('');
    setModalTypes(DOCUMENT_TYPES);
    setShowNewFileModal(false);
  };

  const handleNext = () => {
    if (selectedFileType && modalStep === MODAL_STEPS.SELECT_FILE_TYPE) {
      setModalStep(MODAL_STEPS.FILE_DETAILS);
      if (shouldAutoNumber(selectedFileType)) {
        getNextInvoiceNumber();
      }
    }
  };

  const handleBack = () => {
    if (modalStep === MODAL_STEPS.FILE_DETAILS) {
      setModalStep(MODAL_STEPS.SELECT_FILE_TYPE);
      setSelectedTemplate(null);
      setFileName('');
    }
  };

  const handleAdd = async () => {
    let response;
    let fileType = 'documents';
    const payload = { lead_id: leadId, name: fileName };
    const isTemplate = selectedTemplate && selectedTemplate !== 'from_scratch';

    switch (selectedFileType) {
      case 'invoice':
        fileType = 'invoices';
        payload.orgId = orgId;
        payload.identifier = identifier;
        if (isTemplate) {
          payload.templateId = selectedTemplate;
          payload.isTemplate = true;
        }
        response = await createInvoiceFromLead(payload);
        break;
      case 'quote':
        fileType = 'quotes';
        payload.orgId = orgId;
        payload.identifier = identifier;
        if (projectId) {
          payload.wedding_id = projectId;
        }
        if (isTemplate) {
          payload.templateId = selectedTemplate;
          payload.isTemplate = true;
        }
        response = await createQuoteFromLead(payload);
        break;
      case 'documents':
        // Falls through.
      default:
        payload.kind = selectedFileType;
        if (selectedFileType === 'contract') {
          payload.required_actions = 'Signature and Countersignature';
        } else if (selectedFileType === 'proposal') {
          payload.required_actions = 'Signature';
        } else {
          payload.required_actions = 'None';
        }
        if (projectId) {
          payload.wedding_id = projectId;
        }
        if (isTemplate) {
          payload.document_template_id = selectedTemplate;
          response = await createDocumentFromTemplate(orgId, payload);
        } else {
          payload.status = 'Draft';
          response = await createDocument(orgId, payload);
        }
    }

    if (response.ok && response.data) {
      const { data } = response;
      if (data.USED_IDENTIFIER) {
        // The identifier is already used.
        setError('This number has already been used. Please enter a new number.');
        return;
      }
      if (selectedFileType !== 'invoice' && selectedFileType !== 'quote') {
        const comment = `${selectedFileType} ${fileName} created.`;
        await createDocumentComment(orgId, data.id, comment);
      } else {
        const body = `Created the ${selectedFileType} '${fileName}'`;
        const kind = 'aisleplanner';
        const invoiceId = selectedFileType === 'invoice' ? data.id : null;
        const quoteId = selectedFileType === 'quote' ? data.id : null;
        await createLeadComment(orgId, leadId, body, kind, quoteId, invoiceId);
      }
      handleCloseModal();
      window.open(`/app/pro/${orgId}/${fileType}/${response.data.id}?return_to=lead_files`, '_self');
    } else {
      pushSiteError('Your file couldn’t be created at this time. Please try again in a moment.');
    }
  };

  const handleTemplateChange = (e) => {
    const templateValue = e.target.value;
    if (templateValue === 'no_templates') return;

    setSelectedTemplate(templateValue);

    if (templateValue !== 'from_scratch') {
      const selectedTemplateData = templates.find(template => template.id === parseInt(templateValue, 10));

      if (selectedTemplateData && fileName === '') {
        setFileName(selectedTemplateData.name);
      }
    }
  };

  const getDocumentIcon = (documentKind) => {
    const fileType = FILE_TYPES.find(type => type.value === documentKind);
    return fileType ? fileType.icon : null;
  };

  const onDocumentClick = (documentId) => {
    window.open(`/app/pro/${orgId}/documents/${documentId}?return_to=lead_files&lead_id=${leadId}`, '_self');
  };

  const onInvoiceClick = (invoiceId) => {
    window.open(`/app/pro/${orgId}/invoices/${invoiceId}?return_to=lead_files&lead_id=${leadId}`, '_self');
  };

  const onQuoteClick = (quoteId) => {
    window.open(`/app/pro/${orgId}/quotes/${quoteId}?return_to=lead_files&lead_id=${leadId}`, '_self');
  };

  const handleDocumentPreview = (viewToken) => {
    window.open(`/document/preview/${viewToken}`, '_blank');
  };

  const handleDocumentViewer = (viewToken) => {
    window.open(`/document/viewer/${viewToken}?org-view`);
  };

  const handleDocumentDelete = (documentId) => {
    dispatch(deleteDocument(orgId, documentId, leadId));
  };

  const sortedQuotes = useMemo(() => {
    return [...quotes].sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateB - dateA;
    });
  }, [quotes]);

  const sortedInvoices = useMemo(() => {
    return [...invoices].sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateB - dateA;
    });
  }, [invoices]);

  useEffect(() => {
    if (showNewFileModal) {
      handleOpenModal(FILE_TYPES);
    }
  }, [showNewFileModal]);

  if (isLoading) {
    return (
      <Loading />
    );
  }

  if (!documents.length && !quotes.length && !invoices.length) {
    return (
      <>
        <div className="leads-files-landing">
          <p>
            <b>All Your Lead’s Files, All in One Place. </b>
            From proposals, quotes, and invoices to brochures, contracts, questionnaires,
            and reference docs - keep every important file organized right here with your lead.
            Upload your first file to start building a complete, easy-to-access record,
            so everything you need is always at your fingertips.{' '}
            <a
              href="http://help.aisleplanner.com/en/articles/12162169-accessing-lead-files"
              target="_blank"
              rel="noreferrer"
            >
              <b>Learn more here</b>
            </a>.
          </p>
        </div>
        <br />
        <button
          className="button button--sm"
          onClick={() => handleOpenModal(FILE_TYPES)}
        >
          ADD NEW FILE
        </button>
        <NewDocumentModal
          modalStep={modalStep}
          handleCloseModal={handleCloseModal}
          selectedFileType={selectedFileType}
          selectedTemplate={selectedTemplate}
          fileName={fileName}
          identifier={identifier}
          error={error}
          autoNumberingInvoices={autoNumberingInvoices}
          templates={templates}
          orgId={orgId}
          modalTypes={modalTypes}
          setSelectedFileType={setSelectedFileType}
          handleTemplateChange={handleTemplateChange}
          setFileName={setFileName}
          setIdentifier={setIdentifier}
          setError={setError}
          handleAdd={handleAdd}
          handleBack={handleBack}
          handleNext={handleNext}
        />
      </>
    );
  }

  return (
    <div className="lead-documents">
      <div className="lead-documents__section">
        <div className="documents-title">Documents</div>
        {documents.map((document) => (
          <DocumentItem
            key={`document-item-${document.id}`}
            document={document}
            getDocumentIcon={getDocumentIcon}
            onDocumentClick={onDocumentClick}
            handleDocumentDelete={handleDocumentDelete}
            handleDocumentPreview={handleDocumentPreview}
            handleDocumentViewer={handleDocumentViewer}
          />
        ))}
        <button
          className="button--unstyled button--add gold"
          onClick={() => handleOpenModal(DOCUMENT_TYPES)}
        >
          Add Document
        </button>
      </div>
      <div className="lead-documents__section">
        <div className="documents-title">Quotes</div>
        {sortedQuotes.map(({
          id,
          identifier: _identifier,
          currency,
          name,
          status,
          statusCode,
          total,
          expirationDate,
          isViewed,
        }) => {
          const noDeleteStatuses = ['Accepted', 'Viewed', 'Closed'];
          const showDelete = !(noDeleteStatuses.includes(status) || isViewed) || statusCode === 'expired';
          return (
            <QuoteInvoiceListItem
              key={id}
              id={id}
              type="quote"
              name={name}
              identifier={_identifier}
              currency={currency}
              total={total}
              status={statusCode}
              onClick={onQuoteClick}
              orgId={orgId}
              leadId={leadId}
              expirationDate={expirationDate}
              showDelete={showDelete}
              confirmMessage={(
                <>
                  Deleting this quote will <strong>permanently</strong> remove all
                  of its information. Type “DELETE” to confirm.
                </>
              )}
              confirmText='DELETE'
              requireConfirmation
            />
          );
        })}
        <button
          className="button--unstyled button--add gold"
          onClick={() => handleOpenModal(FILE_TYPES, 'quote')}
        >
          Add Quote
        </button>
      </div>

      <div className="lead-documents__section">
        <div className="documents-title">Invoices</div>
        {sortedInvoices.map(({
          id,
          identifier: _identifier,
          currency,
          name,
          statusCode,
          paid = 0,
          total = 0,
          hasPayments,
          isViewed,
          isClosed,
          sendToken,
          isPublished,
          currentlyPublishedVersion,
          version,
        }) => {
          const showDelete = !(hasPayments || isViewed || isClosed);
          return (
            <QuoteInvoiceListItem
              key={id}
              id={id}
              type="invoice"
              name={name}
              identifier={_identifier}
              currency={currency}
              total={total}
              paid={paid}
              balance={total - paid}
              status={statusCode}
              onClick={onInvoiceClick}
              orgId={orgId}
              leadId={leadId}
              showDelete={showDelete}
              viewToken={sendToken}
              isPublished={isPublished}
              currentlyPublishedVersion={currentlyPublishedVersion}
              version={version}
              confirmMessage={(
                <>
                  Deleting this invoice will <strong>permanently</strong> remove all
                  of its information. Type “DELETE” to confirm.
                </>
              )}
              confirmText='DELETE'
              requireConfirmation
            />
          );
        })}

        <button
          className="button--unstyled button--add gold"
          onClick={() => handleOpenModal(FILE_TYPES, 'invoice')}
        >
          Add Invoice
        </button>
      </div>
      <NewDocumentModal
        modalStep={modalStep}
        handleCloseModal={handleCloseModal}
        selectedFileType={selectedFileType}
        selectedTemplate={selectedTemplate}
        fileName={fileName}
        identifier={identifier}
        error={error}
        autoNumberingInvoices={autoNumberingInvoices}
        templates={templates}
        orgId={orgId}
        modalTypes={modalTypes}
        setSelectedFileType={setSelectedFileType}
        handleTemplateChange={handleTemplateChange}
        setFileName={setFileName}
        setIdentifier={setIdentifier}
        setError={setError}
        handleAdd={handleAdd}
        handleBack={handleBack}
        handleNext={handleNext}
      />
    </div>
  );
};

const DocumentItem = ({
  document,
  getDocumentIcon,
  onDocumentClick,
  handleDocumentDelete,
  handleDocumentPreview,
  handleDocumentViewer,
}) => {
  const documentIcon = getDocumentIcon(document.kind);
  const expirationDate = document.expirationDate ? dayjs(document.expirationDate) : null;
  const status = useMemo(() => {
    if (document.status === 'Voided') {
      return document.status;
    }
    if (document.kind === 'questionnaire' && document.status === 'Accepted') {
      return 'Completed';
    }
    if (document.kind === 'contract') {
      if (document.status === 'Signed') {
        if (document.requiredActions === 'Signature and Countersignature') {
          return 'Co-Sign Needed';
        }
        return 'signed';
      }
      if (document.status === 'Accepted') {
        return 'Completed';
      }
      if (document.documentViews.length > 0 && document.status !== 'Accepted') {
        return 'Viewed';
      }
    }
    if (
      (document.kind === 'brochure' && document.status === 'Accepted') ||
      (
        (document.documentViews.length > 0 && document.status !== 'Accepted') &&
        (document.kind === 'proposal' || document.kind === 'contract' || document.kind === 'questionnaire')
      )
    ) {
      return 'Viewed';
    }
    if (
      (document.status === 'Sent' || document.status === 'Draft') &&
      expirationDate && dayjs().isAfter(expirationDate)
    ) {
      return 'Expired';
    };
    return document.status;
  }, [document.status]);

  const canDeleteByStatus = ['Draft', 'Sent', 'Expired'].includes(document.status);
  const isNoDeleteAfterViewKind = ['questionnaire', 'proposal', 'contract'].includes(document.kind);
  const showDelete = canDeleteByStatus && !(status === 'Viewed' && isNoDeleteAfterViewKind);

  return (
    <div
      key={document.id}
      className={clsx('lead-document', `lead-document--${document.kind}`)}
      onClick={() => onDocumentClick(document.id)}
    >
      <div className="lead-document__icon">
        <img
          src={documentIcon}
          alt={document.kind}
        />
      </div>
      <div className="lead-document__content">
        <div className="lead-document__name">{document.name}</div>
        <div className="lead-document__kind">{document.kind}</div>
      </div>
      <div className={clsx('lead-document__status button', status.toLowerCase())}>
        {status}
      </div>
      <div
        className="lead-document__options"
        onClick={(e) => e.stopPropagation()}
      >
        <DocumentOptions
          onPreview={(viewToken) => handleDocumentPreview(viewToken)}
          onView={(viewToken) => handleDocumentViewer(viewToken)}
          onDelete={() => handleDocumentDelete(document.id)}
          showDelete={showDelete}
          viewers={document.viewers || []}
          viewToken={document.viewToken}
          documentKind={document.kind}
          confirmMessage={(
            <>
              Deleting this document will <strong>permanently</strong> remove all
              of its information. Type "DELETE" to confirm.
            </>
          )}
          confirmText="DELETE"
          requireConfirmation
        />
      </div>
    </div>
  );
};

const NewDocumentModal = ({
  modalStep,
  handleCloseModal,
  selectedFileType,
  selectedTemplate,
  fileName,
  identifier,
  error,
  autoNumberingInvoices,
  templates,
  orgId,
  modalTypes,
  setSelectedFileType,
  handleTemplateChange,
  setFileName,
  setIdentifier,
  setError,
  handleAdd,
  handleBack,
  handleNext,
}) => (
  <DocumentModal
    open={modalStep > 0}
    onClose={handleCloseModal}
    modalStep={modalStep}
    selectedFileType={selectedFileType}
    selectedTemplate={selectedTemplate}
    fileName={fileName}
    identifier={identifier}
    error={error}
    isAutonumbering={autoNumberingInvoices}
    filteredTemplates={templates}
    orgId={orgId}
    types={modalTypes}
    onFileTypeSelect={(fileType) => setSelectedFileType(fileType)}
    onTemplateChange={handleTemplateChange}
    onFileNameChange={(e) => setFileName(e.target.value)}
    onIdentifierChange={(e) => {
      // The only error state is duplicate invoice number,
      // but we'll need to clear error on other events if
      // there are other kinds of errors to handle.
      setError('');
      setIdentifier(e.target.value);
    }}
    onNext={handleNext}
    onBack={handleBack}
    onAdd={handleAdd}
  />
);

export default LeadRecordDocuments;
